/**
 * Utility functions for time conversions in SweldoNow.
 * Handles exact conversions based on a standard 8-hour workday.
 */

/**
 * Converts a decimal days value into a human-readable "Xh Ym" format.
 * Example: 0.5625 days -> "4h 30m"
 * 
 * @param {number|string} daysValue - The fractional day value from the database
 * @returns {string} Formatted hours and minutes string
 */
export function decimalWorkDaysToHoursMinutes(daysValue) {
    const parsedDays = parseFloat(daysValue);
    if (!parsedDays || isNaN(parsedDays) || parsedDays === 0) return '0h 0m';
    
    // 1. Convert days value to total working hours (assuming standard 8-hour shift)
    const totalHoursDecimal = parsedDays * 8;
    
    // 2. Reuse the decimal hours converter to avoid duplicate rounding logic
    return decimalHoursToHoursMinutes(totalHoursDecimal);
}

/**
 * Converts a raw decimal hours value into a human-readable "Xh Ym" format.
 * Example: 4.50 hours -> "4h 30m"
 * 
 * @param {number|string} hoursValue - The decimal hours value (e.g. 4.50)
 * @returns {string} Formatted hours and minutes string
 */
export function decimalHoursToHoursMinutes(hoursValue) {
    const parsedHours = parseFloat(hoursValue);
    if (!parsedHours || isNaN(parsedHours) || parsedHours === 0) return '0h 0m';

    // 1. Extract whole hours
    const hours = Math.floor(parsedHours);
    
    // 2. Extract remaining minutes and round cleanly to avoid floating point issues
    const minutes = Math.round((parsedHours - hours) * 60);
    
    // 3. Handle edge case where minutes round up to 60
    if (minutes === 60) {
        return `${hours + 1}h 0m`;
    }
    
    return `${hours}h ${minutes}m`;
}
